import { definePreset } from '@primeuix/themes';
import AuraBase from '@primeuix/themes/aura/base';
import badge from '@primeuix/themes/aura/badge';
import button from '@primeuix/themes/aura/button';
import dialog from '@primeuix/themes/aura/dialog';
import iconfield from '@primeuix/themes/aura/iconfield';
import inputtext from '@primeuix/themes/aura/inputtext';
import ripple from '@primeuix/themes/aura/ripple';
import select from '@primeuix/themes/aura/select';
import slider from '@primeuix/themes/aura/slider';
import toggleswitch from '@primeuix/themes/aura/toggleswitch';
import virtualscroller from '@primeuix/themes/aura/virtualscroller';

/**
 * The Tracker's PrimeVue preset: Aura's primitive and semantic tokens, the action blue as
 * `primary`, and the tokens of exactly the components the app renders (from stage 4, Settings').
 *
 * @why    Built on `aura/base`, not on `aura`. The full preset carries the tokens of all 97
 *         PrimeVue components, and PrimeVue makes its whole config reactive and deep-watches
 *         it on install, so every token of every component the Tracker will never render is
 *         walked at boot, on the main thread, before the first paint. Measured on the Honor
 *         tablet (Chrome 153, prod build, 11 warm loads each): the plugin install took
 *         101 ms and First Contentful Paint went from 316 to 440 ms. `aura/base` has the same
 *         primitive and semantic tokens and no components, and importing it leaves the other
 *         96 component files out of the bundle too.
 *
 * @rule   A component's tokens enter here in the same change that first renders it —
 *         `import toggleswitch from '@primeuix/themes/aura/toggleswitch'` into `components` —
 *         together with the tokens of the components it renders inside (Select draws an
 *         InputText and a VirtualScroller; Button, a Badge). A component without its tokens
 *         renders unstyled, and `tests/unit/ui.preset.spec.js` says which ones are missing.
 *
 * @why    `primary` is the action blue. Aura's is emerald, and in the Tracker green means
 *         "the order is correct" (norm 11: each colour, one job), so a library button left on
 *         the default would read as a verdict. The blue is the one the app already paints its
 *         actions with (#60A5FA is blue.400, #3B82F6 is blue.500), and Aura's primitive palette
 *         IS Tailwind's, so this is a mapping, not a new colour. Never the bronze of the brand:
 *         inside the product the product's semantic palette rules, not Bronze's.
 *
 * @rule   One colour scheme, the dark one: every `light-dark(a, b)` Aura writes becomes its `b`
 *         (`darkOnly` below), in the semantic tokens and in every component set that enters.
 * @why    Aura 5 writes its colours with the CSS `light-dark()` function, which is Chrome 123,
 *         and four tablets of the fleet run 108–122: there a declaration with it is invalid and
 *         the library components would come out with no colour at all. The Tracker is always
 *         dark, so the light branch was never going to paint; dropping it loses nothing, and
 *         with it the question of where the dark-mode class goes (decided when stage 2 closed,
 *         measured on 5.0.1: 59 `light-dark()` in the generated theme and 208 in the tokens of
 *         the 12 components of the map, all to 0).
 *
 * Migration stage 4 put the first components on screen, in Settings: ToggleSwitch, Slider, Select
 * and Button, with the components they draw inside (Select: IconField, InputText, VirtualScroller;
 * Button: Badge; both: Ripple, which stays off); stage 5 added Dialog, as the frame of the four
 * deliberate overlays (src/ui/components/BzDialog.vue). Their tokens are `COMPONENT_TOKENS` below.
 *
 * The design system (phase 2, docs/sistema-diseno/README.md) gives them its tokens, screen by screen:
 * stage 1 moves Settings and the blocking notification, and with them the switch, the slider, the
 * select, the dialog's card and the button kinds those two draw. What the kitchen needs from them
 * that is not a token — a 48 px target, Android's back closing an open list — is in the wrappers of
 * src/ui/components/.
 *
 * Base font size: PrimeVue 5 sizes in rem against a 16 px root and ships `aura-compat` for
 * apps on 14 px. The Tracker never changed the root size, so it is 16 px and plain Aura fits.
 */
/** `light-dark(a, b)` → `b`, wherever it appears in a token value. */
export function darkBranch(value) {
    let out = value;
    for (let at = out.indexOf('light-dark('); at >= 0; at = out.indexOf('light-dark(')) {
        const open = at + 'light-dark('.length;
        let depth = 1, comma = -1, i = open;
        for (; i < out.length && depth; i++) {
            if (out[i] === '(') depth++;
            else if (out[i] === ')') depth--;
            else if (out[i] === ',' && depth === 1 && comma < 0) comma = i;
        }
        if (depth || comma < 0) throw new Error(`light-dark() without two branches: ${value}`);
        out = out.slice(0, at) + out.slice(comma + 1, i - 1).trim() + out.slice(i);
    }
    return out;
}

/** The preset with only its dark scheme: every string token through `darkBranch`. */
export function darkOnly(node) {
    if (typeof node === 'string') return darkBranch(node);
    if (Array.isArray(node)) return node.map(darkOnly);
    if (node && typeof node === 'object') return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, darkOnly(v)]));
    return node;
}

/**
 * The components' own tokens, over Aura's: the design system's (src/ui/tokens.css) — the Tracker's own
 * look, with the behaviour Android and iOS share.
 *
 * @rule   A component a screen on the design system draws takes the system's tokens (`var(--bz-…)`) and
 *         its sizes from its scales: the switch, the slider, the select, the dialog and the five kinds
 *         of BzButton. docs/sistema-diseno/README.md says what each is for.
 * @rule   Every screen that draws a library component is on the system since 8-oct-2026 (Debug and the
 *         login moved in stage 4): there is no production value left to keep here.
 * @why    `var(--bz-…)` and not a hex: `tests/unit/ui.tokens.spec.js` and `ui.system.spec.js` see these
 *         uses too, and the contrast of every pair is computed there, not trusted.
 * @why    Every colour of a variant a screen uses is set here, hover and active included: Aura writes
 *         some of them with `color-mix()`, which is Chrome 111, and the fleet has tablets on 108. Hover
 *         and active keep the resting colour: a tablet has no hover, and the press is the app's own (a
 *         scale and a fade, App.vue), the same on every button.
 */
export const COMPONENT_TOKENS = {
    // The switch both systems share: a filled pill, grey off and blue on, and a white thumb that only
    // slides (BzToggle moves it). No outline, no icon, no thumb that changes size. 52 × 32 is Material 3's
    // track (iOS's is 51 × 31); the thumb fills it as iOS's does, 2 px in.
    toggleswitch: {
        root: {
            width: '52px',
            height: '32px',
            borderRadius: 'var(--bz-radius-full)',
            gap: '2px',
            shadow: 'none',
            borderWidth: '0px',
            borderColor: 'transparent',
            hoverBorderColor: 'transparent',
            checkedBorderColor: 'transparent',
            checkedHoverBorderColor: 'transparent',
            background: 'var(--bz-control)',
            hoverBackground: 'var(--bz-control)',
            checkedBackground: 'var(--bz-action)',
            checkedHoverBackground: 'var(--bz-action)',
            transitionDuration: 'var(--bz-duration-fast)',
        },
        handle: {
            size: 'var(--bz-thumb)',
            borderRadius: '50%',
            background: 'var(--bz-handle)',
            hoverBackground: 'var(--bz-handle)',
            checkedBackground: 'var(--bz-handle)',
            checkedHoverBackground: 'var(--bz-handle)',
        },
    },
    // A thin track and a round white thumb. The track and its active part are BzSlider's own (an active
    // part from the centre); PrimeVue's are transparent.
    slider: {
        track: { background: 'transparent', borderRadius: 'var(--bz-radius-full)', size: 'var(--bz-slider-track)' },
        range: { background: 'transparent' },
        handle: {
            width: 'var(--bz-thumb)',
            height: 'var(--bz-thumb)',
            borderRadius: '50%',
            background: 'var(--bz-handle)',
            hoverBackground: 'var(--bz-handle)',
            content: {
                width: 'var(--bz-thumb)',
                height: 'var(--bz-thumb)',
                borderRadius: '50%',
                background: 'var(--bz-handle)',
                hoverBackground: 'var(--bz-handle)',
                shadow: 'none',
            },
        },
    },
    // A value in a list row, not a field: no box, the chosen value at 16 in the second ink (a step under
    // the row's title, one over its hint) and an unfold icon (BzSelect makes the whole row its target);
    // its list a menu that floats over the screen (the raised surface, its edge, a menu's shadow), its
    // options at 400 like the rows, the chosen one in the selection's blue and checked (BzSelect adds the
    // check). An option's corners are concentric with the menu's: its radius is the menu's minus the
    // menu's edge and padding (16 - 1 - 8), so the two curves keep one distance all the way round. With a
    // keyboard, the system's ring (BzSelect).
    select: {
        root: {
            background: 'transparent',
            borderColor: 'transparent',
            hoverBorderColor: 'transparent',
            focusBorderColor: 'transparent',
            color: 'var(--bz-ink-2)',
            fontSize: 'var(--bz-text-md)',
            fontWeight: 'var(--bz-weight-body)',
            paddingX: 'var(--bz-space-2)',
            paddingY: 'var(--bz-space-3)',
            borderRadius: 'var(--bz-radius-md)',
            shadow: 'none',
            // The field and its options change colour at once, as a native one does. A 150 ms colour
            // transition running while the list comes in made the harness rasterise the card under the
            // list one way or another by the run (tests/visual/screens.visual.spec.js).
            transitionDuration: '0s',
            focusRing: { width: '0px', shadow: 'none' },
        },
        dropdown: { width: 'var(--bz-space-6)', color: 'var(--bz-ink-2)' },
        overlay: {
            background: 'var(--bz-raised)',
            borderColor: 'var(--bz-edge)',
            borderRadius: 'var(--bz-radius-lg)',
            color: 'var(--bz-ink)',
            shadow: 'var(--bz-shadow-menu)',
        },
        list: { padding: 'var(--bz-space-2)', gap: 'var(--bz-space-1)' },
        option: {
            fontSize: 'var(--bz-text-md)',
            fontWeight: 'var(--bz-weight-body)',
            padding: 'var(--bz-space-3) var(--bz-space-4)',
            borderRadius: 'calc(var(--bz-radius-lg) - 1px - var(--bz-space-2))',
            color: 'var(--bz-ink)',
            focusColor: 'var(--bz-ink)',
            focusBackground: 'var(--bz-control)',
            selectedColor: 'var(--bz-ink)',
            selectedFocusColor: 'var(--bz-ink)',
            selectedBackground: 'var(--bz-action-container)',
            selectedFocusBackground: 'var(--bz-action-container)',
        },
    },
    // The dialog: what floats (the raised surface and its edge), the verdict's 20 px corners and a short
    // shadow (BzDialog's `card`). The frame-only dialogs not yet on the system draw nothing of this.
    dialog: {
        root: {
            background: 'var(--bz-raised)',
            borderColor: 'var(--bz-edge)',
            color: 'var(--bz-ink)',
            borderRadius: 'var(--bz-radius-xl)',
            shadow: 'var(--bz-shadow-overlay)',
        },
        // No `title`: BzDialog draws its own header (eyebrow, title, subtitle), so PrimeVue's title
        // element is never rendered.
        // A little more room between the title and the body, so the body breathes.
        header: { padding: 'var(--bz-space-6) var(--bz-space-6) var(--bz-space-5)', gap: 'var(--bz-space-3)' },
        content: { padding: '0 var(--bz-space-6) var(--bz-space-6)' },
        footer: { padding: '0 var(--bz-space-6) var(--bz-space-6)', gap: 'var(--bz-target-gap)' },
    },
    // BzButton's five kinds: primary (the verdict's blue), secondary (the control grey), outlined, text
    // and danger (red text, as both systems draw a destructive action).
    button: {
        root: {
            paddingX: '16px',
            paddingY: '14px',
            borderRadius: 'var(--bz-radius-md)',
            gap: 'var(--bz-space-2)',
            label: { fontWeight: 'var(--bz-weight-label)' },
            // No transitionDuration: App.vue's `transition` on every button (transform and opacity, the
            // app's press) replaces PrimeVue's colour transitions, so a duration here would never apply.
            focusRing: { width: '3px', style: 'solid', offset: '2px' },
            primary: {
                background: 'var(--bz-action)',
                hoverBackground: 'var(--bz-action)',
                activeBackground: 'var(--bz-action)',
                borderColor: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: 'transparent',
                color: 'var(--bz-on-action)',
                hoverColor: 'var(--bz-on-action)',
                activeColor: 'var(--bz-on-action)',
                focusRing: { color: 'var(--bz-focus)', shadow: 'none' },
            },
            secondary: {
                background: 'var(--bz-control)',
                hoverBackground: 'var(--bz-control)',
                activeBackground: 'var(--bz-control)',
                borderColor: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: 'transparent',
                color: 'var(--bz-ink)',
                hoverColor: 'var(--bz-ink)',
                activeColor: 'var(--bz-ink)',
                focusRing: { color: 'var(--bz-focus)', shadow: 'none' },
            },
            // The danger kind is a text button (below); here only its focus ring, the system's.
            danger: {
                focusRing: { color: 'var(--bz-focus)', shadow: 'none' },
            },
        },
        outlined: {
            secondary: {
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                borderColor: 'var(--bz-outline)',
                color: 'var(--bz-ink)',
            },
        },
        text: {
            primary: {
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                color: 'var(--bz-action-ink)',
            },
            danger: {
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                color: 'var(--bz-danger)',
            },
        },
    },
};

export const BronzePreset = darkOnly(definePreset(AuraBase, {
    components: { badge, button, dialog, iconfield, inputtext, ripple, select, slider, toggleswitch, virtualscroller },
}, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}',
        },
        // One focus indicator for every library control: 3 px in the focus blue, 2 px out, on the
        // screen's surface and not on the control's fill. A keyboard lights it; a finger does not.
        focusRing: { width: '3px', style: 'solid', color: 'var(--bz-focus)', offset: '2px', shadow: 'none' },
        // The scrim behind a dialog on the system: production's decisions' black at 60 %, flat, no blur
        // (src/ui/tokens.css).
        mask: { background: 'var(--bz-scrim)', color: 'var(--bz-ink)' },
    },
    components: COMPONENT_TOKENS,
}));
